import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export interface Pagination {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
}

// ---------------------------------------------------------------------------
// Admin Users
// ---------------------------------------------------------------------------

export type AdminRole = "super_admin" | "manager" | "support" | "finance" | "logistics";

export interface AdminUser {
  readonly id: string;
  readonly full_name: string;
  readonly email: string;
  readonly role: AdminRole;
  readonly is_active: boolean;
  readonly mfa_enabled: boolean;
  readonly last_login_at: string | null;
  readonly created_at: string;
  readonly permissions: readonly string[];
}

export interface AdminUserListResponse {
  readonly users: readonly AdminUser[];
  readonly pagination: Pagination;
}

export interface AdminUserListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly role?: AdminRole | null;
  readonly is_active?: boolean | null;
  readonly q?: string | null;
}

export interface CreateAdminUserInput {
  readonly full_name: string;
  readonly email: string;
  readonly role: AdminRole;
  readonly permissions: readonly string[];
}

export interface UpdateAdminUserInput {
  readonly full_name?: string;
  readonly role?: AdminRole;
  readonly permissions?: readonly string[];
  readonly is_active?: boolean;
  readonly mfa_enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export interface RolePermission {
  readonly resource: string;
  readonly actions: readonly string[];
}

export interface Role {
  readonly id: string;
  readonly name: string;
  readonly label: string;
  readonly description: string;
  readonly permissions: readonly RolePermission[];
  readonly user_count: number;
  readonly is_system: boolean;
}

export interface RoleListResponse {
  readonly roles: readonly Role[];
}

export interface CreateRoleInput {
  readonly name: string;
  readonly label: string;
  readonly description: string;
  readonly permissions: readonly RolePermission[];
}

export type UpdateRoleInput = Partial<CreateRoleInput>;

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export type TaskPriority = "low" | "medium" | "high" | "urgent";
export type TaskStatus = "todo" | "in_progress" | "done" | "cancelled";

export interface Task {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  readonly priority: TaskPriority;
  readonly status: TaskStatus;
  readonly assigned_to_id: string | null;
  readonly assigned_to_name: string | null;
  readonly due_date: string | null;
  readonly created_at: string;
  readonly completed_at: string | null;
}

export interface TaskListResponse {
  readonly tasks: readonly Task[];
  readonly pagination: Pagination;
}

export interface TaskListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: TaskStatus | null;
  readonly priority?: TaskPriority | null;
  readonly assigned_to_id?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface CreateTaskInput {
  readonly title: string;
  readonly description?: string;
  readonly priority: TaskPriority;
  readonly assigned_to_id?: string;
  readonly due_date?: string;
}

export type UpdateTaskInput = Partial<{
  readonly title: string;
  readonly description: string | null;
  readonly priority: TaskPriority;
  readonly status: TaskStatus;
  readonly assigned_to_id: string | null;
  readonly due_date: string | null;
}>;

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

export type ShiftStatus = "scheduled" | "active" | "completed" | "absent";

export interface Shift {
  readonly id: string;
  readonly agent_name: string;
  readonly agent_id: string;
  readonly region: string;
  readonly start_time: string;
  readonly end_time: string | null;
  readonly status: ShiftStatus;
  readonly check_in_at: string | null;
  readonly check_out_at: string | null;
  readonly notes: string | null;
}

export interface ShiftListResponse {
  readonly shifts: readonly Shift[];
  readonly pagination: Pagination;
}

export interface ShiftListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly region?: string | null;
  readonly agent_id?: string | null;
  readonly status?: ShiftStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface CreateShiftInput {
  readonly agent_id: string;
  readonly region: string;
  readonly start_time: string;
  readonly end_time: string;
  /** Optional explicit date (YYYY-MM-DD). If omitted, derived server-side from start_time. */
  readonly date?: string;
}

// ---------------------------------------------------------------------------
// Agent Performance
// ---------------------------------------------------------------------------

export interface AgentPerformanceMetrics {
  readonly agent_id: string;
  readonly agent_name: string;
  readonly region: string;
  readonly visits_this_month: number;
  readonly orders_taken: number;
  readonly revenue_generated_fcfa: number;
  readonly avg_order_value_fcfa: number;
  readonly on_time_checkins_percent: number;
  readonly target_achievement_percent: number;
  readonly rank: number;
}

export interface AgentPerformanceResponse {
  readonly agents: readonly AgentPerformanceMetrics[];
  readonly period: string;
}

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

export type SLAStatusValue = "ok" | "warning" | "breach";

export interface SLAConfig {
  readonly id: string;
  readonly metric: string;
  readonly label: string;
  readonly target_minutes: number;
  readonly warning_threshold_percent: number;
}

export interface SLAStatus {
  readonly metric: string;
  readonly label: string;
  readonly current_avg_minutes: number;
  readonly target_minutes: number;
  readonly compliance_percent: number;
  readonly status: SLAStatusValue;
}

export interface SLAResponse {
  readonly configs: readonly SLAConfig[];
  readonly statuses: readonly SLAStatus[];
}

// ---------------------------------------------------------------------------
// Incidents
// ---------------------------------------------------------------------------

export type IncidentSeverity = "low" | "medium" | "high" | "critical";
export type IncidentStatus = "open" | "investigating" | "resolved" | "closed";

export interface Incident {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly severity: IncidentSeverity;
  readonly status: IncidentStatus;
  readonly reported_by: string;
  readonly assigned_to: string | null;
  readonly created_at: string;
  readonly resolved_at: string | null;
}

export interface IncidentListResponse {
  readonly incidents: readonly Incident[];
  readonly pagination: Pagination;
}

export interface IncidentListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly severity?: IncidentSeverity | null;
  readonly status?: IncidentStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface CreateIncidentInput {
  readonly title: string;
  readonly description: string;
  readonly severity: IncidentSeverity;
  readonly assigned_to?: string;
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export interface Message {
  readonly id: string;
  readonly sender_name: string;
  readonly subject: string;
  readonly body: string;
  readonly recipients: readonly string[];
  readonly is_read: boolean;
  readonly sent_at: string;
}

export interface MessageListResponse {
  readonly messages: readonly Message[];
  readonly unread_count: number;
  readonly pagination: Pagination;
}

export interface MessageListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly folder?: "inbox" | "sent" | null;
}

export interface SendMessageInput {
  readonly subject: string;
  readonly body: string;
  readonly recipient_ids: readonly string[];
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const operationsApi = {
  adminUsers: {
    list: (params: AdminUserListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        role: params.role,
        is_active: params.is_active,
        q: params.q,
      });
      return adminFetch<AdminUserListResponse>(`/api/admin/operations/users${qs}`);
    },

    create: (input: CreateAdminUserInput) =>
      adminFetch<{ user: AdminUser }>("/api/admin/operations/users", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    update: (id: string, input: UpdateAdminUserInput) =>
      adminFetch<{ user: AdminUser }>(
        `/api/admin/operations/users/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),

    deactivate: (id: string) =>
      adminFetch<{ user: AdminUser }>(
        `/api/admin/operations/users/${encodeURIComponent(id)}/deactivate`,
        { method: "PATCH" },
      ),

    resetPassword: (id: string) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/operations/users/${encodeURIComponent(id)}/reset-password`,
        { method: "POST" },
      ),
  },

  roles: {
    list: () => adminFetch<RoleListResponse>("/api/admin/operations/roles"),

    create: (input: CreateRoleInput) =>
      adminFetch<{ role: Role }>("/api/admin/operations/roles", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    update: (id: string, input: UpdateRoleInput) =>
      adminFetch<{ role: Role }>(
        `/api/admin/operations/roles/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),

    delete: (id: string) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/operations/roles/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  tasks: {
    list: (params: TaskListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        status: params.status,
        priority: params.priority,
        assigned_to_id: params.assigned_to_id,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<TaskListResponse>(`/api/admin/operations/tasks${qs}`);
    },

    create: (input: CreateTaskInput) =>
      adminFetch<{ task: Task }>("/api/admin/operations/tasks", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    update: (id: string, patch: UpdateTaskInput) =>
      adminFetch<{ task: Task }>(
        `/api/admin/operations/tasks/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(patch) },
      ),
  },

  shifts: {
    list: (params: ShiftListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        region: params.region,
        agent_id: params.agent_id,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<ShiftListResponse>(`/api/admin/operations/shifts${qs}`);
    },

    create: (input: CreateShiftInput) =>
      adminFetch<{ shift: Shift }>("/api/admin/operations/shifts", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },

  performance: {
    get: (period?: string) => {
      const qs = period ? `?period=${encodeURIComponent(period)}` : "";
      return adminFetch<AgentPerformanceResponse>(
        `/api/admin/operations/performance${qs}`,
      );
    },
  },

  sla: {
    get: () => adminFetch<SLAResponse>("/api/admin/operations/sla"),

    update: (id: string, target_minutes: number) =>
      adminFetch<{ config: SLAConfig }>(
        `/api/admin/operations/sla/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify({ target_minutes }) },
      ),
  },

  incidents: {
    list: (params: IncidentListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        severity: params.severity,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<IncidentListResponse>(`/api/admin/operations/incidents${qs}`);
    },

    create: (input: CreateIncidentInput) =>
      adminFetch<{ incident: Incident }>("/api/admin/operations/incidents", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    resolve: (id: string) =>
      adminFetch<{ incident: Incident }>(
        `/api/admin/operations/incidents/${encodeURIComponent(id)}/resolve`,
        { method: "PATCH" },
      ),
  },

  messages: {
    list: (params: MessageListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        folder: params.folder,
      });
      return adminFetch<MessageListResponse>(`/api/admin/operations/messages${qs}`);
    },

    send: (input: SendMessageInput) =>
      adminFetch<{ message: Message }>("/api/admin/operations/messages", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    markRead: (id: string) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/operations/messages/${encodeURIComponent(id)}/read`,
        { method: "PATCH" },
      ),
  },
} as const;
